/**
 * Endpoints de recepción — confirmar y cancelar.
 *
 * Una "recepción" es una operación tipo `recepcion` creada cuando el destino de
 * una transferencia (o agente externo) confirma haber recibido el producto. Esto
 * lo informa al SNT con `tipoMensaje: 'confirmarTransaccion'` y propaga la
 * confirmación al despacho original (FIX CR-4).
 */

import { z } from 'zod';
import { OperacionSchema } from '@/api/contracts/operaciones';
import { getDb, nextMovId, nextOpId } from '@/api/mock/db';
import { logApi } from '@/stores/apilog';
import { ApiError } from './auth';

const MOCK_LATENCY_MS = 150;
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// =============================================================================
// CONFIRMAR RECEPCIÓN
// =============================================================================

export const ConfirmarRecepcionRequest = z.object({
  /** ID de la operación de despacho que se está recepcionando. */
  opOrigenId: z.string().min(1),
  /** Usuario que confirma. */
  usuarioId: z.string().min(1),
  /** GLN donde se está recepcionando (debe coincidir con `destinoUbicacionId` del despacho). */
  ubicacionId: z.string().min(1),
  /** Evento ANMAT de la recepción (RECEPCION DE PRODUCTO DESDE UN ESLABON ANTERIOR). */
  eventoAnmatId: z.number().int().positive(),
  /**
   * Modo de recepción:
   *  - `directa`: confirma todo el remito sin verificar serie por serie.
   *  - `verificar_scan`: el operador escaneó series una por una (modo estricto).
   * Por ahora solo soportamos `directa`; `verificar_scan` queda para Fase 3+.
   */
  modo: z.enum(['directa', 'verificar_scan']).default('directa'),
});

export const ConfirmarRecepcionResponse = z.object({
  operacion: OperacionSchema,
});

export type ConfirmarRecepcionRequest = z.infer<typeof ConfirmarRecepcionRequest>;
export type ConfirmarRecepcionResponse = z.infer<typeof ConfirmarRecepcionResponse>;

export async function confirmarRecepcion(
  input: ConfirmarRecepcionRequest,
): Promise<ConfirmarRecepcionResponse> {
  const req = ConfirmarRecepcionRequest.parse(input);
  await delay(MOCK_LATENCY_MS);

  const db = getDb();
  const opOrigen = db.operaciones.find((o) => o.id === req.opOrigenId);
  if (!opOrigen)
    throw new ApiError(`Operación origen ${req.opOrigenId} no encontrada`, 'OP_NOT_FOUND');
  if (opOrigen.tipo !== 'despacho_transferencia') {
    throw new ApiError(
      'Solo se pueden recepcionar transferencias entre ubicaciones',
      'OP_NOT_TRANSFER',
    );
  }
  if (opOrigen.destinoUbicacionId !== req.ubicacionId) {
    throw new ApiError(
      `La operación no tiene como destino tu ubicación activa (${req.ubicacionId})`,
      'OP_DESTINO_MISMATCH',
    );
  }
  // No permitir recepcionar dos veces
  const yaRecepcionada = db.operaciones.some(
    (o) =>
      o.tipo === 'recepcion' && o.opOrigenId === opOrigen.id && o.anmat.estado !== 'cancelada',
  );
  if (yaRecepcionada) {
    throw new ApiError('Esta operación ya fue recepcionada', 'OP_ALREADY_RECEIVED');
  }

  const ahora = new Date().toISOString();
  const opId = nextOpId();
  const recepcion = {
    id: opId,
    tipo: 'recepcion' as const,
    ubicacionId: req.ubicacionId,
    opOrigenId: opOrigen.id,
    operacionOrigenId: opOrigen.id,
    remitoNumero: opOrigen.remitoNumero,
    eventoAnmatId: req.eventoAnmatId,
    fecha: ahora,
    usuarioId: req.usuarioId,
    seriesIds: [...opOrigen.seriesIds],
    estado: 'cerrada' as const,
    anmat: {
      tipoMensaje: 'confirmarTransaccion' as const,
      estado: 'en_cola' as const,
      intentos: 0,
      codigoTransaccion: null,
      mensajes: [
        { ts: ahora, tipo: 'request' as const, body: 'confirmarTransaccion enviado al SNT' },
      ],
    },
    pdfGeneradoAt: null,
  };
  db.operaciones.push(recepcion);

  // Actualizar tenencia de series: en_transito → en_poder en el GLN destino.
  // Agregar movimiento 'recepcion' por cada una.
  for (const sid of opOrigen.seriesIds) {
    const serie = db.series.find((s) => s.id === sid);
    if (!serie) continue;
    serie.tenencia = 'en_poder';
    serie.ubicacionActualId = req.ubicacionId;
    db.movimientos.push({
      id: nextMovId(),
      serieId: sid,
      operacionId: opId,
      tipoEvento: 'recepcion',
      fecha: ahora,
      ubicacionId: req.ubicacionId,
      usuarioId: req.usuarioId,
      codigoTransaccion: null,
      eventoAnmatId: req.eventoAnmatId,
      detalle: `Recepción ${req.modo === 'verificar_scan' ? 'con verificación por escaneo' : 'todo-o-nada'} · origen ${opOrigen.id}`,
    });
  }

  // (v13) Log del POST de confirmación al SNT
  logApi({
    sistema: 'snt',
    metodo: 'POST',
    endpoint: '/snt/confirmarTransaccion',
    status: 200,
    operacionId: opId,
    requestBody: {
      opOrigenId: opOrigen.id,
      eventoAnmatId: req.eventoAnmatId,
      cantSeries: opOrigen.seriesIds.length,
      modo: req.modo,
    },
    responseBody: { estado: 'aceptado_en_cola' },
  });

  // Simular respuesta del SNT (2s). FIX CR-4: al confirmar la recepción
  // propagamos `confirmada` al despacho origen.
  setTimeout(() => {
    const recOp = db.operaciones.find((o) => o.id === opId);
    if (!recOp) return;
    if (recOp.anmat.estado === 'cancelada') return; // FIX CR-1
    const codigoTx = 'TX-' + Math.random().toString(36).slice(2, 10).toUpperCase();
    recOp.anmat.estado = 'confirmada';
    recOp.anmat.codigoTransaccion = codigoTx;
    recOp.anmat.mensajes.push({
      ts: new Date().toISOString(),
      tipo: 'response',
      body: `OK · ${codigoTx}`,
    });
    for (const m of db.movimientos) {
      if (m.operacionId === opId && !m.codigoTransaccion) m.codigoTransaccion = codigoTx;
    }
    // FIX CR-4: propagar a la op original
    const origen = db.operaciones.find((o) => o.id === opOrigen.id);
    if (origen && origen.anmat.estado === 'informada') {
      origen.anmat.estado = 'confirmada';
      origen.anmat.mensajes.push({
        ts: new Date().toISOString(),
        tipo: 'response',
        body: `Confirmada por contraparte (recepción ${opId} · ${codigoTx})`,
      });
    }
  }, 2000);

  return ConfirmarRecepcionResponse.parse({ operacion: recepcion });
}

// =============================================================================
// CANCELAR RECEPCIÓN
// =============================================================================

export const CancelarRecepcionRequest = z.object({
  operacionId: z.string().min(1),
  usuarioId: z.string().min(1),
  razon: z.string().min(1, 'Razón obligatoria para cancelar'),
});

export const CancelarRecepcionResponse = z.object({
  operacion: OperacionSchema,
});

export type CancelarRecepcionRequest = z.infer<typeof CancelarRecepcionRequest>;
export type CancelarRecepcionResponse = z.infer<typeof CancelarRecepcionResponse>;

export async function cancelarRecepcion(
  input: CancelarRecepcionRequest,
): Promise<CancelarRecepcionResponse> {
  const req = CancelarRecepcionRequest.parse(input);
  await delay(MOCK_LATENCY_MS);

  const db = getDb();
  const op = db.operaciones.find((o) => o.id === req.operacionId);
  if (!op) throw new ApiError(`Operación ${req.operacionId} no encontrada`, 'OP_NOT_FOUND');
  if (op.tipo !== 'recepcion') {
    throw new ApiError('Solo se cancelan recepciones desde este endpoint', 'OP_NOT_RECEPCION');
  }
  if (op.anmat.estado === 'cancelada') {
    throw new ApiError('La recepción ya fue cancelada', 'OP_ALREADY_CANCELLED');
  }

  // Defensa: no permitir cancelar si alguna serie ya fue movida después
  // (ej. ya se hizo un despacho desde la ubicación destino).
  const seriesMovidas = op.seriesIds.filter((sid) => {
    const movsPosteriores = db.movimientos.filter(
      (m) =>
        m.serieId === sid &&
        new Date(m.fecha).getTime() > new Date(op.fecha).getTime() &&
        m.operacionId !== op.id,
    );
    return movsPosteriores.length > 0;
  });
  if (seriesMovidas.length > 0) {
    throw new ApiError(
      `${seriesMovidas.length} series ya tienen movimientos posteriores · no se puede cancelar`,
      'SERIES_HAVE_LATER_MOVES',
    );
  }

  const ahora = new Date().toISOString();
  const opOrigen = op.opOrigenId
    ? db.operaciones.find((o) => o.id === op.opOrigenId)
    : null;

  // Revertir: series vuelven a en_transito en la ubicación de origen
  for (const sid of op.seriesIds) {
    const serie = db.series.find((s) => s.id === sid);
    if (!serie) continue;
    if (serie.tenencia === 'en_poder' && serie.ubicacionActualId === op.ubicacionId) {
      serie.tenencia = 'en_transito';
      if (opOrigen) serie.ubicacionActualId = opOrigen.ubicacionId;
    }
    db.movimientos.push({
      id: nextMovId(),
      serieId: sid,
      operacionId: op.id,
      tipoEvento: 'cancelacion_recepcion',
      fecha: ahora,
      ubicacionId: op.ubicacionId,
      usuarioId: req.usuarioId,
      codigoTransaccion: null,
      eventoAnmatId: null,
      detalle: `Cancelación de recepción · razón: ${req.razon}`,
    });
  }

  // Si la recepción estaba `confirmada`, el despacho origen también fue
  // marcado como `confirmada` por el CR-4 propagation. Al cancelar la
  // recepción, esa confirmación deja de ser válida — revertimos el despacho
  // origen a `informada`. Esto refleja el estado real en SNT después de
  // procesar la cancelación: el despacho ya no tiene contraparte que confirmó.
  // (Paquete N)
  let despachoOrigenRevertido = false;
  if (op.anmat.estado === 'confirmada' && opOrigen && opOrigen.anmat.estado === 'confirmada') {
    opOrigen.anmat.estado = 'informada';
    opOrigen.anmat.mensajes.push({
      ts: ahora,
      tipo: 'response',
      body: `Confirmación revertida — la recepción ${op.id} fue cancelada (razón: ${req.razon})`,
    });
    despachoOrigenRevertido = true;
  }

  op.anmat.estado = 'cancelada';
  op.anmat.mensajes.push({
    ts: ahora,
    tipo: 'request',
    body: `cancelarTransaccion (recepción) · razón: ${req.razon}`,
  });
  op.fechaCancelacion = ahora;
  // Limpiamos el pdfGeneradoAt (deja de ser válido)
  op.pdfGeneradoAt = null;

  // (v13) Log de la cancelación
  logApi({
    sistema: 'snt',
    metodo: 'POST',
    endpoint: '/snt/cancelarTransaccion',
    status: 200,
    operacionId: op.id,
    requestBody: {
      codigoTransaccion: op.anmat.codigoTransaccion,
      motivo: req.razon,
      contexto: 'recepcion',
      eraConfirmada: despachoOrigenRevertido,
    },
    responseBody: {
      estado: 'cancelada',
      anuladoEnSnt: true,
      despachoOrigenRevertido,
    },
  });

  return CancelarRecepcionResponse.parse({ operacion: op });
}
